import type { ReactNode } from 'react'
import type { Appointment } from '@/types/clinic'

import Link from 'next/link'
import { useState } from 'react'
import { CalendarSearch, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { formatTime, formatWeekdayDate, relativeDayLabel } from '@/lib/dates'
import { clampPage, paginate } from '@/lib/list'
import { useLookups, usePhoneFormatter } from '@/hooks/use-clinic-helpers'
import { useAppDialogs } from '@/components/dialogs/app-dialogs'
import EmptyState from '@/components/app/empty-state'
import IconAction from '@/components/app/icon-action'
import Pagination from '@/components/app/pagination'
import PatientAvatar from '@/components/app/patient-avatar'
import AppointmentStatusMenu from '@/components/appointments/appointment-status-menu'
import AppointmentActionsMenu from '@/components/appointments/appointment-actions-menu'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

interface AppointmentListProps {
    appointments: Appointment[]
    emptyAction?: ReactNode
}

const headClass = 'text-xs font-semibold tracking-wide text-muted-foreground uppercase'

const AppointmentList = ({ appointments, emptyAction }: AppointmentListProps): ReactNode => {
    const { today } = useClinic()
    const { patientsById } = useLookups()
    const formatPhone = usePhoneFormatter()
    const { openWhatsApp } = useAppDialogs()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const currentPage = clampPage(page, appointments.length, pageSize)
    const rows = paginate(appointments, currentPage, pageSize)

    if (appointments.length === 0) {
        return <EmptyState icon={CalendarSearch} title='No appointments found' description='Change the date range or filters, or book a new appointment.' action={emptyAction} />
    }

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow className='hover:bg-transparent'>
                        <TableHead className={`${headClass} pl-4`}>Date and time</TableHead>
                        <TableHead className={headClass}>Patient</TableHead>
                        <TableHead className={`${headClass} hidden md:table-cell`}>Treatment</TableHead>
                        <TableHead className={headClass}>Status</TableHead>
                        <TableHead className={`${headClass} hidden xl:table-cell`}>Notes</TableHead>
                        <TableHead className='pr-4 text-right'><span className='sr-only'>Actions</span></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(appointment => {
                        const patient = patientsById.get(appointment.patientId)
                        const relative = relativeDayLabel(appointment.date, today)
                        return (
                            <TableRow key={appointment.id} className={cn(appointment.status === 'cancelled' && 'opacity-70')}>
                                <TableCell className='pl-4'>
                                    <p className='font-medium'>{formatWeekdayDate(appointment.date)}</p>
                                    <p className='text-xs text-muted-foreground'>
                                        {formatTime(appointment.time)} · {appointment.duration} min
                                        {(relative === 'Today' || relative === 'Tomorrow') && <span className='ml-1 font-medium text-primary'>{relative}</span>}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    {patient
                                        ? (
                                            <div className='flex items-center gap-3'>
                                                <PatientAvatar name={patient.name} />
                                                <div className='min-w-0'>
                                                    <Link href={`/patients/${patient.id}`} className='block max-w-[12rem] truncate font-medium hover:text-primary hover:underline'>{patient.name}</Link>
                                                    <p className='text-xs text-muted-foreground'>{formatPhone(appointment.phone)}</p>
                                                </div>
                                            </div>
                                        )
                                        : <span className='text-muted-foreground'>Removed patient</span>}
                                </TableCell>
                                <TableCell className='hidden max-w-[14rem] truncate md:table-cell'>{appointment.treatmentName}</TableCell>
                                <TableCell><AppointmentStatusMenu appointment={appointment} /></TableCell>
                                <TableCell className='hidden max-w-[16rem] truncate text-muted-foreground xl:table-cell'>{appointment.notes || '-'}</TableCell>
                                <TableCell className='pr-4 text-right'>
                                    <div className='flex items-center justify-end'>
                                        <IconAction
                                            label='WhatsApp patient'
                                            icon={MessageCircle}
                                            tone='whatsapp'
                                            onClick={() => openWhatsApp({ patientId: appointment.patientId, kind: appointment.date >= today ? 'appointmentReminder' : 'custom', appointmentId: appointment.id })}
                                        />
                                        <AppointmentActionsMenu appointment={appointment} />
                                    </div>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>

            <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={appointments.length}
                onPageChange={setPage}
                onPageSizeChange={size => {
                    setPageSize(size)
                    setPage(1)
                }}
                itemLabel='appointments'
            />
        </>
    )
}

export default AppointmentList
